import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import HideFields from '@/assets/icons/hide-fields.svg?react'
import Button from '@/components/Button/Button'
import CheckboxGroup, {
  type CheckboxGroupOption
} from '@/components/Checkbox/CheckboxGroup'
import FormField from '@/components/FormField'
import ModalForm from '@/components/Modal/ModalForm'

import styles from './ColumnsFilter.module.scss'

export type ColumnsFilterProps = {
  options: CheckboxGroupOption[]
  activeColumnKeys: string[]
  onChangeActiveColumnKeys: (value: string[]) => void
}

const ColumnsFilter = (props: ColumnsFilterProps) => {
  const { t } = useTranslation('common')

  const form = useForm({
    defaultValues: { columns: props.activeColumnKeys },
    resolver: zodResolver(
      z.object({
        columns: z
          .array(z.string())
          .min(1, { message: t('error.at-least-one-column') })
      })
    )
  })

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenChange = (isOpen: boolean) => {
    setIsModalOpen(isOpen)
    form.reset({ columns: props.activeColumnKeys })
  }

  const close = () => {
    handleOpenChange(false)
  }

  return (
    <ModalForm
      title={t('header.columns')}
      form={form}
      open={isModalOpen}
      onCancel={close}
      onClose={close}
      onOpenChange={handleOpenChange}
      trigger={
        <Button variant="tertiary" icon={<HideFields />}>
          {t('button.columns')}
        </Button>
      }
      submitText={t('button.apply')}
      onSubmit={({ columns }) => {
        props.onChangeActiveColumnKeys(columns)
        setIsModalOpen(false)
      }}
    >
      <FormField
        id="active-column-keys"
        control={form.control}
        name="columns"
        hideLabel
        render={({ inputProps }) => (
          <CheckboxGroup
            options={props.options}
            legend={
              <legend className={styles.checkboxGroupLegend}>
                {t('label.view-columns')}
              </legend>
            }
            hideAllOption
            {...inputProps}
          />
        )}
        className={styles.modalBody}
      />
    </ModalForm>
  )
}

export default ColumnsFilter
